import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Divider, Tab, Typography, Grid } from '@mui/material';
import { Box, styled } from '@mui/system';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import DOMPurify from 'dompurify';

import { ReactComponent as Regenerate } from 'common/assets/images/ai-generate.svg';
import { ReactComponent as OpenInNewIcon } from 'common/assets/images/new-tab-icon.svg';
import { ReactComponent as SaveIcon } from 'common/assets/images/save-Icon.svg';
import AIFeedback from 'common/components/AIFeedback/AIFeedback';
import ButtonComp from 'common/components/Button/ButtonComp';
import { Loader } from 'common/components/Loader/Loader';
import PromptSelect from 'common/components/PromptSelect/PromptSelect';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { FEEDBACK_OBJECT_TYPE, MODULES } from 'common/constants';
import {
	AI_SIMULATION_GENAI_DISCLAIMER,
	AI_SIMULATION_MODEL_KEYS,
} from 'common/constants/AISimulationConstants';
import { BUTTON_TEXT } from 'common/constants/DataIngestionConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	regInventoryHorizonSummaryTab,
	SUMMARY_ERROR_MESSAGE,
} from 'common/constants/RegulationInventoryConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { setRegInvSummaryRegenerated } from 'common/store/reducer/regulationInventoryReducer';
import { urlWithTenant } from 'common/utils/utilFunctions';

import {
	getRegSummaryDetailsData,
	getRegeneratedPromptSummary,
	getRegeneratedSummary,
	updateChangeSummaryText,
} from 'features/HorizonScanning/api';
import {
	IRegDetailsResponse,
	IStyledTabProps,
} from 'features/HorizonScanning/api/type-def';

import styles from './SummaryDisplay.module.scss';

const { horizonScanning, horizonScanningDetail } = rrcpDashboardRoutes;

interface ISummaryDisplayProps {
	changeAISummary: string;
	relevantObligation: string;
	regId: string;
	groupId?: string;
}

const { DISCLAIMER } = AI_SIMULATION_GENAI_DISCLAIMER;
const { HORIZON_SCAN_AND_ALERTS } = MODULES;
const { OK } = HTTP_STATUS_CODES;
const { REGULATION_INVENTORY_CHANGE_SUMMARY } = FEEDBACK_OBJECT_TYPE;
const { REGENERATE, SAVE } = BUTTON_TEXT;
const { REGENERATE_ERROR, SAVE_ERROR, SAVE_SUCCESS, REGENERATE_SUCCESS } =
	SUMMARY_ERROR_MESSAGE;

const SummaryDisplay = (props: ISummaryDisplayProps) => {
	const { changeAISummary, relevantObligation, regId, groupId } = props;
	const {
		whiteTextColor,
		spacing2,
		spacing4,
		spacing5,
		focustabColor,
		tabsTextColor,
		tabSelectedTextColor,
		selectedTabBackground,
		tabBorderRadius,
		summaryTabBackground,
		activeIconColorPrimary,
	} = styles;
	const [selectedTabValue, setSelectedTabValue] =
		useState<string>('changeSummary');
	const [regDetailsData, setRegDetailsData] = useState<IRegDetailsResponse>();
	const dispatch = useDispatch();
	const { HORIZON_CHANGE_SUMMARY_KEY } = AI_SIMULATION_MODEL_KEYS;

	const [isChangeSummaryRegenerated, setIsChangeSummaryRegenerated] =
		useState<boolean>(false);
	const [changeSummaryText, setChangeSummaryText] =
		useState<string>(changeAISummary);
	const [isAPILoading, setIsAPILoading] = useState<boolean>(false);

	const isSummaryRegenerated = useSelector(
		(state: RootState) => state.regulationInventory.regInvAISummaryRegenerated,
	);

	const style = {
		'.MuiButtonBase-root': {
			backgroundColor: summaryTabBackground,
			border: 'none',
		},
		'.MuiButtonBase-root.MuiTab-root.Mui-selected': {
			color: tabSelectedTextColor,
		},
		'.Mui-selected': {
			background: `${focustabColor} !important`,
			color: tabsTextColor,
			borderRadius: tabBorderRadius,
		},
		'.MuiTabs-flexContainer': {
			backgroundColor: `${summaryTabBackground} !important`,
		},
		'.MuiTabs-indicator': {
			display: 'none',
		},
	};

	const navigate = useNavigate();
	const handleClick = () => {
		navigate(
			urlWithTenant(`/${horizonScanning}/${horizonScanningDetail}/${regId}`),
		);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		event.stopPropagation();
		setSelectedTabValue(newValue);
	};

	const fetchRegDetailsData = async () => {
		const res = await getRegSummaryDetailsData(regId || '');
		if (res?.statusCode === OK) {
			setRegDetailsData(res?.responseBody);
		}
		if (res?.errorBody || res?.error) {
			const { errorBody } = res;
			const responseError: any = res.error;
			const errorMessage =
				(errorBody?.errorMessage as string) ||
				(responseError?.message as string);
			dispatch(
				setToastMessage({
					message: errorMessage,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const StyledSummaryTab = styled((props: IStyledTabProps) => (
		<Tab disableRipple {...props} />
	))({
		textTransform: 'none',
		fontSize: spacing5,
		color: tabsTextColor,
		borderRadius: tabBorderRadius,
		padding: `${spacing2} ${spacing4}`,
		'&.Mui-selected': {
			color: whiteTextColor,
			background: selectedTabBackground,
		},
		'& .MuiTabs-indicator': {
			backgroundColor: selectedTabBackground,
		},
		'&.Mui-focusVisible': {
			backgroundColor: selectedTabBackground,
		},
	});
	const regeneratePromptClick = async (event: any) => {
		const promptKey = event.key;
		regenerateSummaryDetails(promptKey);
	};
	const regenerateButtonClick = async () => {
		regenerateSummaryDetails('');
	};
	const regenerateSummaryDetails = async (promptKey: string) => {
		setIsAPILoading(true);
		const text = regDetailsData?.aiSummary || '';
		const response = promptKey.length
			? await getRegeneratedPromptSummary(promptKey, relevantObligation, text)
			: await getRegeneratedSummary(relevantObligation, text);

		if (response.statusCode === OK && response.responseBody) {
			setIsChangeSummaryRegenerated(true);
			dispatch(setRegInvSummaryRegenerated(true));
			setChangeSummaryText(response.responseBody?.answer);
			dispatch(
				setToastMessage({
					message: REGENERATE_SUCCESS,
					severity: 'success',
					isToastOpen: true,
				}),
			);
		} else {
			setIsChangeSummaryRegenerated(false);
			dispatch(
				setToastMessage({
					message: REGENERATE_ERROR,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsAPILoading(false);
	};

	const handleSaveClick = async () => {
		setIsAPILoading(true);
		const response = await updateChangeSummaryText(
			groupId || '',
			changeSummaryText,
		);
		if (response.statusCode === OK) {
			setIsChangeSummaryRegenerated(false);
			dispatch(
				setToastMessage({
					message: SAVE_SUCCESS,
					severity: 'success',
					isToastOpen: true,
				}),
			);
		} else {
			dispatch(
				setToastMessage({
					message: SAVE_ERROR,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsAPILoading(false);
	};

	useEffect(() => {
		fetchRegDetailsData();
	}, [regId]);

	const showChangeSummaryOutput = () =>
		!isAPILoading && (
			<Grid item xs={12} sm={12}>
				<Box className={styles['tab-sub-header']}>
					{regInventoryHorizonSummaryTab[0]}
				</Box>
				{changeSummaryText ? (
					<div
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(
								changeSummaryText.replace(/\n/g, '<br />'),
							),
						}}
					/>
				) : (
					<Box className={styles['no-data-found']}>No summary found</Box>
				)}
			</Grid>
		);

	const feedbackPrompt = JSON.stringify({
		original_obligation: relevantObligation,
		reg_changes: regDetailsData?.aiSummary || '',
	});

	return (
		<Box>
			<TabContext value={selectedTabValue}>
				<Box className={styles['tab-container']}>
					<Typography className={styles['section-header']}>Overview</Typography>
					<TabList
						sx={style}
						onChange={(event: React.SyntheticEvent, value: string) =>
							handleTabChange(event, value)
						}
						className={styles['panel-layout']}
					>
						<StyledSummaryTab
							label={regInventoryHorizonSummaryTab[0]}
							value="changeSummary"
						/>
						<StyledSummaryTab
							label={regInventoryHorizonSummaryTab[1]}
							value="obligation"
						/>
					</TabList>
					<Box className={styles['horizon-link-regenerate']}>
						{selectedTabValue === 'changeSummary' && (
							<PromptSelect
								aiModelKey={HORIZON_CHANGE_SUMMARY_KEY}
								btnType="secondary"
								groupClass={styles['regenerate-button']}
								compClass={styles['regenerate-button']}
								iconClass={styles['regenerate-button-prompt']}
								startIcon={
									<Box className={styles['regenerate-button-icon']}>
										<Regenerate />
									</Box>
								}
								onSelectPrompt={regeneratePromptClick}
								clickHandler={regenerateButtonClick}
								data-testid="regenerate-button"
							>
								{REGENERATE}
							</PromptSelect>
						)}
						<Box onClick={handleClick} className={styles['horizon-link']}>
							Go to horizon scanning
							<OpenInNewIcon className={styles['horizon-link-icon']} />
						</Box>
					</Box>
				</Box>
				<Box>
					<TabPanel value="changeSummary" className={styles['tab-style']}>
						<Grid container spacing={2}>
							{isChangeSummaryRegenerated && !isAPILoading ? (
								<Grid
									container
									direction="column"
									className={styles['textfield-container']}
								>
									<TextFieldWithLabel
										inputProps={{
											'data-testid': 'summary-edit-area',
											className: styles['summary-textarea'],
										}}
										id="summary-edit-area"
										value={changeSummaryText}
										onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
											setChangeSummaryText(event.target.value);
										}}
										multiLine
										showLabel={false}
										error={false}
										helperText=""
										className={styles['summary-edit-area']}
									/>
									<div className={styles['button-container']}>
										<ButtonComp
											btnType="primary"
											startIcon={<SaveIcon fill={activeIconColorPrimary} />}
											compClass={styles['ai-summary-btn']}
											clickHandler={handleSaveClick}
											data-testId="save-button"
										>
											{SAVE}
										</ButtonComp>
									</div>
								</Grid>
							) : (
								showChangeSummaryOutput()
							)}
							{isAPILoading && (
								<Loader show className={styles['no-data-found']} />
							)}
						</Grid>
					</TabPanel>
					<TabPanel value="obligation" className={styles['tab-style']}>
						<Grid container spacing={2}>
							<Grid item>
								<Box className={styles['tab-sub-header']}>
									{regInventoryHorizonSummaryTab[1]}
								</Box>
								<div
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(
											relevantObligation.replace(/\n/g, '<br />'),
										),
									}}
								/>
							</Grid>
						</Grid>
					</TabPanel>
				</Box>
			</TabContext>
			{selectedTabValue === 'changeSummary' && (
				<div className={styles['disclaimer-wrapper']}>
					<div className={styles['disclaimer-inner-wrapper']}>
						<Typography className={styles['disclaimer-text']}>
							{DISCLAIMER}
						</Typography>
					</div>
					<AIFeedback
						objectType={REGULATION_INVENTORY_CHANGE_SUMMARY}
						module={HORIZON_SCAN_AND_ALERTS}
						objectData={{
							prompt: feedbackPrompt,
							response: changeSummaryText,
							reference: regId,
						}}
						shouldResetFeedback={isSummaryRegenerated}
					/>
				</div>
			)}
			<Divider />
		</Box>
	);
};

export default SummaryDisplay;
